// ============ ENVIRONMENTAL HAZARD ENGINE ============
// Turns a world's ALREADY-SIMULATED environment into per-second pressure on the player's HEALTH, and on the OXYGEN
// RESERVE that stands between an airless world and that health.
//
// IT INVENTS NO PHYSICS. system-view's atmosphereFor() already produces a physically-grounded atmosphere —
// pressureMbar, per-gas fractions, greenhouseDeltaC, airless — from escape velocity, volatile supply, cold-trap
// freeze-out and greenhouse forcing. Re-deriving any of that here would give two models that disagree, and the
// player would see an atmosphere read-out that contradicts what is actually hurting them. This module is a pure
// FUNCTION of that output.
//
// PURE AND DEPENDENCY-FREE on purpose: no THREE, no DOM, no imports. That is what lets it be unit-tested in node
// (see functions/test/hazard.test.mjs) rather than only by standing on a planet and waiting to die.
//
// ── ONE GAUGE ────────────────────────────────────────────────────────────────────────────────────────────────────
// There used to be four: oxygen, a suit battery, suit integrity and health, of which the HUD showed health plus the
// WORST of the other three under one "LIFE" bar. That arrangement had three problems and they compounded:
//   * NOTHING EVER REFILLED THEM. Oxygen came back only on a breathable world — of which the galaxy has essentially
//     one — and no code path anywhere restored the battery or integrity. So a player who took any damage at all
//     carried it forever and the only reset was dying, which made suicide the cheapest repair in the game.
//   * The battery sat between temperature and health, so "how long can I stand here" depended on a hidden number.
//   * The bar showed a MINIMUM of three unrelated systems, so it fell for reasons the player could not see.
// Now every hazard damages HEALTH directly, and each keeps its own signature — anoxia is fast and immediate, cold
// is a slow bleed that scales with how far out of band you are, corrosion is slower still. The cause is in the
// toast; the cost is in the one bar. Health also REGENERATES whenever nothing is hurting you, which is what makes
// retreating to safety the correct move rather than a way to die more slowly.
//
// ── AND ONE RESERVE, WHICH IS NOT A SECOND GAUGE (launch plan PHY-05) ───────────────────────────────────────────────
// Vacuum used to take health from the first tick, on the 69% of rocky worlds that have no air, starting the moment you
// land. The suit now carries an OXYGEN RESERVE, five minutes of breathing where there is nothing to breathe, and
// suffocation starts only once it is empty. It is built against each of the three problems above:
//   * IT REFILLS, wherever a player naturally goes: in the ship (orbit, the system and galaxy views, anywhere off a
//     world's surface), in the asteroid flyer, and in breathable air. Thirty seconds from empty to full, and ten in
//     the ship (O2_SHIP_REFILL_S), which mends health too. Nothing is carried forever, and dying is never the
//     cheapest way to get it back.
//   * IT IS VISIBLE, as TIME ("O₂ 4:32" beside the hearts), which is the unit "how long can I stay here" is asked in.
//     No hidden number sits between vacuum and health: the reserve IS the number on screen.
//   * THE CAUSE IS NAMED. While the reserve lasts, vacuum is a warning that costs nothing; a minute before it runs out
//     the suit says so; once it is empty the hazard taking health is "Out of oxygen", in the health bar's title and in
//     the toast, so a falling health bar always says why.
// Health is still the only thing that kills you. The reserve only decides WHEN vacuum starts to. And it is spent only
// where there is no air at all: a thin or unbreathable atmosphere is still scrubbed for free (see evaluateHazards), so
// the reserve neither drains nor refills there. The worlds most of the game is played on cost exactly what they did.
//
// The sign convention throughout: rates are PER SECOND and signed the way they move the gauge. Negative drains.

// A YOUNG STAR'S FLARES (N7): every world of a nebula has one every eight minutes, a minute long, and a half-minute warning before.
// …ONE CLOCK PER NEBULA (round 2, ND3, flares you can see: Oct 29): its phase from the SYSTEM's address, so the ring of light the
// system view sends out ahead of a flare reaches every world of it as one flare; every player in it sees the same one. A flare takes 1.2 health a second from a
// player out in it (72 over the minute, from 100): under a roof, or with a Shield module in a suit slot, nothing.
export const FLARE = { PERIOD_S: 480, LENGTH_S: 60, WARN_S: 30, DPS: 1.2 };
export function flareAt(addr, nowMs) {
  let h = 2166136261 >>> 0; const s = String(addr || '').split('.').slice(0, 2).join('.');
  for (let i = 0; i < s.length; i++) h = Math.imul(h ^ s.charCodeAt(i), 16777619);
  const t = ((nowMs / 1000 + ((h >>> 0) % FLARE.PERIOD_S)) % FLARE.PERIOD_S + FLARE.PERIOD_S) % FLARE.PERIOD_S;
  const on = t < FLARE.LENGTH_S;
  return { on, nextIn: on ? 0 : FLARE.PERIOD_S - t, endsIn: on ? FLARE.LENGTH_S - t : 0 };
}
export const HAZARD = {
  // Thermal comfort band. Inside it, temperature costs nothing at all.
  COMFORT_MIN_C: -20,
  COMFORT_MAX_C: 45,
  // Damage scales with how far OUTSIDE the band you are, not with absolute temperature — 50 °C is a nuisance and
  // 400 °C is an emergency, and a flat rate could not express that. The cap is what sets the floor on time-to-die:
  // at 10 %/s an inferno kills a full-health player in 10 s, and the cap is reached 200 °C outside the band, so
  // lava-class worlds are lethal in seconds while a few degrees of chill is a bleed you can ignore for minutes.
  THERMAL_PER_DEG: 0.05,
  THERMAL_MAX: 10,

  // Pressure. The three bands are unchanged; what changed is that the middle one is explicitly SAFE.
  VACUUM_MBAR: 50,                  // below this the suit is a closed system and the reserve only falls
  BREATHABLE_MBAR: 200,             // at or above this AND enough O₂ → you are simply breathing
  BREATHABLE_O2_FRACTION: 0.15,
  CRUSH_MBAR: 5000,                 // above this the shell is being squeezed

  // ── THE OXYGEN RESERVE (launch plan PHY-05) ────────────────────────────────────────────────────────────────────
  // Held as a percentage, like health (ls.o2, 0..100), so it is stored and bounded the same way; these are the
  // seconds that percentage means. o2Seconds() converts, and every threshold below is written in seconds because
  // that is the unit the player reads it in.
  //
  // WHY FIVE MINUTES. It is one excursion: land, look around, walk somewhere, mine and place a few dozen blocks, then
  // come back up. MEASURED by landing a fresh suit on every rocky world of two real samples (galaxy 930718 positions
  // 1-2000 and galaxy 3422 positions 1-1000: 30,108 worlds, each environment built the way system-view's
  // computeBodyEnv builds it) and stepping this engine on foot at the game's 5 Hz. A world counts as EXPLORABLE for
  // N minutes if you can spend N minutes on foot there without losing any health:
  //                                              N = 1 to 5 min   N = 10 min   still alive after 5 min
  //   before: vacuum took 1 %/s from landing          3.3%            3.3%              4.3%
  //   after:  a five-minute reserve first            14.6%            3.3%             17.6%
  // 68.9% of rocky worlds are near-vacuum, and the 11.3 points gained are the ones whose ONLY problem was the air.
  // Past the reserve's length nothing changes, which is the design: the answer to a long stay is a trip back up to
  // refill, not a bigger tank. (With 30 s refills that is about nine tenths of a session on foot.) What still stops
  // the other 85% is temperature: an airless rock is rarely inside the comfort band.
  O2_RESERVE_S: 300,
  // WHY THIRTY SECONDS TO REFILL, ten times faster than it drains: going back up is the price of a long excursion,
  // and the price is the trip (the ascent, the landing, finding your spot again), not a minute of staring at a
  // number. It is the rate in the flyer and in breathable air; the ship itself is faster (O2_SHIP_REFILL_S, below).
  O2_REFILL_S: 30,
  // A minute out, the suit says so, once, and the read-out turns amber. Leaving is instant (the ship seals the moment
  // you lift off), so a minute is plenty of warning without nagging through the first four.
  O2_LOW_S: 60,
  // ANOXIA: health, but only once the reserve is EMPTY. 1 %/s, ~100 s from full health: the rate the landing stopgap
  // (PHY-04) chose so a landing gave you time to look around. The reserve gives that time now, so this is the grace
  // after it, and it is kept generous on purpose: by the time it bites the player has had five minutes, a warning and
  // a read-out at 0:00, and a new player who has not yet learned what O₂ means still gets a hundred seconds to find
  // the way back up. Still faster than corrosion, which stays the mildest hazard.
  ANOXIA_DAMAGE: 1.0,
  // Crush is CAPPED for the same reason thermal is, and it matters more here because the pressure range the generator
  // produces is enormous: a Venus-analog sits at 92 bar, which uncapped would be >100 %/s — dead inside one frame,
  // before the warning toast could even paint. Capped, the worst world in the galaxy still gives you a few seconds to
  // realise and leave, which is the difference between a hazard and a loading screen.
  CRUSH_PER_KMBAR: 0.35,            // %/s per 1000 mbar above CRUSH_MBAR
  CRUSH_MAX: 8,

  // Corrosion. Chlorine is the reactive gas the generator actually produces; the others are inert or breathable.
  // The slowest hazard by design: it is a reason to leave, not a reason to die on the spot.
  CORROSIVE: { Chlorine: 1.0 },
  CORROSION_PER_FRACTION: 0.8,      // %/s of health at 100% concentration, scaled linearly by fraction — kept under
                                    // ANOXIA_DAMAGE so corrosion stays the mildest hazard, as designed

  // RECOVERY. Only while nothing is hurting you. ~67 s from near-death to full: fast enough that a safe world is a
  // genuine sanctuary, slow enough that you cannot tank a hazard by stepping in and out of it.
  REGEN: 1.5,

  // ── THE SHIP IS THE SAFE PLACE (QA F-43) ──────────────────────────────────────────────────────────────────────────
  // Orbit used to hold health exactly where it was and refill the reserve at the walking rate, and said neither: a
  // player who fled a freezing night side at 64 health sat at 64 in orbit with nothing on screen, and was back to full
  // only after landing somewhere mild, which nothing told them either. Going back up is the answer every warning gives
  // ("press L"), so the ship must be where that answer pays: the suit is plugged into the ship's supply, the tank is full
  // in ten seconds and health mends at 2.5 %/s, forty seconds from empty to full. Faster than the ground's REGEN on
  // purpose: the ground is where you recover between jobs, the ship is where you go because something nearly killed you.
  // Not a loophole: leaving and landing again is itself the cost (the dive, finding your spot), and the hazard still has
  // the whole of the next stay to work on you.
  SHIP_REGEN: 2.5,
  O2_SHIP_REFILL_S: 10,

  // ── HEALTH'S OWN WARNINGS (QA F-30) ───────────────────────────────────────────────────────────────────────────────
  // The reserve had two lines (a minute left, and empty) and health had none. A cold that drained 1.3 %/s killed in
  // eighty seconds while the only sign was the "suit straining" toast it had shown since landing, because severity
  // followed the RATE alone. Now the health LEVEL counts too: at HEALTH_LOW any hazard taking health is critical, and
  // crossing it and HEALTH_CRITICAL are reported once each (healthEvent), as the reserve's lines are.
  HEALTH_LOW: 30,
  HEALTH_CRITICAL: 15,
};

// Fraction of a named gas in the mix. `gases` is atmosphereFor's [{name, fraction}] — already normalised to 1.
function fractionOf(gases, name) {
  if (!Array.isArray(gases)) return 0;
  for (const g of gases) if (g && g.name === name) return g.fraction || 0;
  return 0;
}

// The two gauges, read defensively. A missing or malformed value is FULL, never empty: a legacy document, a guest, or
// a state built by hand must not read as dead or as suffocating. Strictly a number: the global isFinite coerces, and
// null would pass it as 0 — a suit reading as empty because a field was null.
const pctOf = (v) => (typeof v === 'number' && Number.isFinite(v) ? Math.max(0, Math.min(100, v)) : 100);
const healthOf = (ls) => pctOf(ls && ls.health);
const o2Of = (ls) => pctOf(ls && ls.o2);

// Seconds of vacuum the reserve still covers. The read-out's number, and the unit every threshold is written in.
export function o2Seconds(ls) { return (o2Of(ls) / 100) * HAZARD.O2_RESERVE_S; }

// A new reserve level, clamped, with the float dust at either end swept off. Fifteen hundred ticks of a drain that is
// not a round number in binary leave ~1e-13 in the tank, which read as "still breathing" for one extra tick and put
// the empty moment a tick late. A billionth of a percent is three nanoseconds of air.
const o2Clamp = (v) => (v < 1e-9 ? 0 : v > 100 - 1e-9 ? 100 : v);
// At or under the warning line, with the same allowance for dust: a reserve at 60.00000003 s IS one minute, and the
// read-out (gauge.js formatO2) already shows it as 1:00, so the warning must not wait another tick to agree.
const o2Low = (pct) => (pct / 100) * HAZARD.O2_RESERVE_S <= HAZARD.O2_LOW_S + 1e-6;

// The reserve's rates, in % of the reserve per second.
const o2Drain = () => 100 / HAZARD.O2_RESERVE_S;
const o2Refill = () => 100 / HAZARD.O2_REFILL_S;

// How far outside the comfort band, in °C. 0 while comfortable.
export function thermalExcess(tempC) {
  if (!isFinite(tempC)) return 0;
  if (tempC > HAZARD.COMFORT_MAX_C) return tempC - HAZARD.COMFORT_MAX_C;
  if (tempC < HAZARD.COMFORT_MIN_C) return HAZARD.COMFORT_MIN_C - tempC;
  return 0;
}

// The environment as the engine sees it:
//   { tempC, pressureMbar, gases:[{name,fraction}], airless, sheltered?, submerged? }
// `submerged` is the head under a liquid (a sea, a lake or a poured body): whatever the air above is, there is none to
// breathe down here, so it is treated like vacuum — the reserve drains (the owner, Oct 6 playtest: `underwater_o2: drain`).
// and the player's life support:
//   { health, o2 }   — both 0..100; o2 is the reserve as a percentage of O2_RESERVE_S
// Returns the signed per-second health and reserve rates, what the suit is breathing, and a HUMAN-READABLE hazard list
// for the HUD. Computing the list here rather than in the UI keeps the warning and the damage from ever disagreeing
// about what is happening.
//
// `breathing` is the reserve's reason, for the read-out's title:
//   'reserve' — no air here, and the suit is drawing on the reserve (it drains)
//   'none'    — no air here and the reserve is empty (suffocating: this is what takes health)
//   'suit'    — a thin or unbreathable atmosphere the suit scrubs for free (the reserve holds)
//   'air'     — breathable air (the reserve refills)
//   'ship'    — sheltered in the flyer (the reserve refills); stepSealed reports the same for orbit
export function evaluateHazards(env, ls) {
  const tempC = env && isFinite(env.tempC) ? env.tempC : 20;
  const airless = !!(env && env.airless);
  const sheltered = !!(env && env.sheltered);
  const submerged = !sheltered && !!(env && env.submerged);   // a pilot breathes the craft's air, wherever the craft is
  const P = airless ? 0 : Math.max(0, (env && env.pressureMbar) || 0);
  const gases = (env && env.gases) || [];
  const health = healthOf(ls);
  const o2 = o2Of(ls);

  const hazards = [];
  let dHealth = 0, dO2 = 0, breathing;

  // ---- Thermal ----
  const excess = thermalExcess(tempC);
  if (excess > 0) {
    const hot = tempC > HAZARD.COMFORT_MAX_C;
    const rate = Math.min(HAZARD.THERMAL_MAX, excess * HAZARD.THERMAL_PER_DEG);
    dHealth -= rate;
    // Severity tracks the RATE, not merely the fact of being out of band: a two-degree chill and a lava field are
    // both "outside the band" and the player needs to be told which one they are standing in.
    // …and the LABEL says which, too: one degree out of band used to read "Extreme cold — suit failing" exactly like
    // two hundred.
    // …AND THE HEALTH IT HAS LEFT TO TAKE (QA F-30). A mild-sounding cold at 25 health is an emergency, so at
    // HEALTH_LOW it is critical whatever its rate, and says what to do; the rate alone still decides "Extreme".
    const extreme = rate >= 2, low = health <= HAZARD.HEALTH_LOW;
    const critical = extreme || low;
    hazards.push({ id: hot ? 'heat' : 'cold', severity: critical ? 'critical' : 'warn',
                   label: extreme ? (hot ? 'Extreme heat — suit failing' : 'Extreme cold — suit failing')
                        : low ? (hot ? 'Too hot — health low, get to shade or orbit' : 'Too cold — health low, get to shelter or orbit')
                              : (hot ? 'Too hot — suit straining' : 'Too cold — suit straining') });
  }

  // ---- Pressure / breathability, and the oxygen reserve ----
  const o2Fraction = fractionOf(gases, 'Oxygen');
  const breathable = !submerged && !airless && P >= HAZARD.BREATHABLE_MBAR && o2Fraction >= HAZARD.BREATHABLE_O2_FRACTION;
  const vacuum = airless ? 'Vacuum' : 'Near-vacuum';
  if (submerged) {
    // UNDER WATER, as in vacuum: the reserve carries you and then you drown. Thick air above the water changes nothing,
    // and the thin-air rule below (PHY-05: any air of 50 mbar or more is the suit's to scrub) is only for air.
    if (o2 > 0) {
      breathing = 'reserve';
      dO2 = -o2Drain();
      const low = o2Low(o2);
      hazards.push({ id: 'underwater', severity: low ? 'critical' : 'warn',
                     label: low ? 'Oxygen low — get your head above water' : 'Underwater — breathing from your reserve' });
    } else {
      breathing = 'none';
      dHealth -= HAZARD.ANOXIA_DAMAGE;
      hazards.push({ id: 'anoxia', severity: 'critical', label: 'Out of oxygen — drowning' });
    }
  } else if (airless || P < HAZARD.VACUUM_MBAR) {
    if (sheltered) {
      // IN THE FLYER the pilot breathes the craft's air, so there is nothing to suffocate on and the reserve tops up.
      // The vacuum is still REPORTED — what is outside is what a pilot wants to know — as the warning it is.
      breathing = 'ship';
      hazards.push({ id: 'vacuum', severity: 'warn', label: vacuum + ' outside — no air to breathe' });
    } else if (o2 > 0) {
      // ON FOOT, the reserve carries you, and costs no health while it does. It escalates to critical a minute out,
      // which is when the one warning that matters is due; stepLifeSupport reports that crossing as an event too,
      // because the toast shows only the worst hazard and a cold world's frostbite would otherwise hide it.
      breathing = 'reserve';
      dO2 = -o2Drain();
      const low = o2Low(o2);
      hazards.push({ id: 'vacuum', severity: low ? 'critical' : 'warn',
                     label: low ? 'Oxygen low — head back up to orbit to refill' : vacuum + ' — breathing from your oxygen reserve' });
    } else {
      breathing = 'none';
      dHealth -= HAZARD.ANOXIA_DAMAGE;
      hazards.push({ id: 'anoxia', severity: 'critical', label: 'Out of oxygen — suffocating' });
    }
  } else if (!breathable) {
    // Between the two thresholds: an atmosphere exists but is not breathable. The suit scrubs it and this costs
    // NOTHING — which is what makes "thin but present" a meaningfully different place to be, and what keeps the
    // ordinary worlds of this game habitable. Most worlds live in this band; making it lethal would make the game
    // unplayable, not tense. The reserve HOLDS here: the suit is breathing the air, not the tank, and it takes
    // breathable air (or the ship) to fill the tank back up.
    breathing = sheltered ? 'ship' : 'suit';
    hazards.push({ id: 'unbreathable', severity: 'warn', label: 'Atmosphere not breathable — the suit is scrubbing it' });
  } else {
    breathing = sheltered ? 'ship' : 'air';
  }
  if (breathing === 'ship' || breathing === 'air') dO2 = o2 < 100 ? o2Refill() : 0;

  if (P > HAZARD.CRUSH_MBAR) {
    dHealth -= Math.min(HAZARD.CRUSH_MAX, ((P - HAZARD.CRUSH_MBAR) / 1000) * HAZARD.CRUSH_PER_KMBAR);
    hazards.push({ id: 'crush', severity: 'critical', label: 'Crushing pressure — suit failing' });
  }

  // ---- Corrosion ----
  let corrosion = 0;
  for (const name in HAZARD.CORROSIVE) corrosion += fractionOf(gases, name) * HAZARD.CORROSIVE[name];
  if (corrosion > 0 && !airless) {
    dHealth -= corrosion * HAZARD.CORROSION_PER_FRACTION;
    hazards.push({ id: 'corrosive', severity: health <= HAZARD.HEALTH_LOW ? 'critical' : 'warn', label: 'Corrosive atmosphere — suit degrading' });   // critical once health is low, as heat and cold are (QA F-30)
  }

  // ---- A young star's flares (the owner's Black Holes and Nebulas picks, Oct 27, N7) ----
  // On a nebula's world the host says when a flare is up and whether you are out in it (env.flare: no roof over you and no
  // Shield module), and how long until the next (env.flareIn). Out in one, health falls for its minute; before one, a warning.
  if (env && env.flare) {
    dHealth -= FLARE.DPS;
    hazards.push({ id: 'flare', severity: health <= HAZARD.HEALTH_LOW ? 'critical' : 'warn', label: 'A young star is flaring — get under a roof or fit a Shield' });
  } else if (env && Number.isFinite(env.flareIn) && env.flareIn > 0 && env.flareIn <= FLARE.WARN_S && !env.flareSafe) {
    hazards.push({ id: 'flareSoon', severity: 'warn', label: `A young star flares in ${Math.ceil(env.flareIn)} s — get under a roof` });
  }

  // ---- Recovery ----
  // Only when NOTHING is taking health. An "unbreathable" warning is not damage, so a thin-air world still heals
  // you; that is deliberate, because it is the environment most of the game is played in.
  // Breathing from the reserve is not being hurt, so a player on their reserve in a temperate vacuum heals: the
  // reserve is the suit coping, and "nothing is taking health" is the one condition regeneration has ever needed.
  const healing = dHealth === 0 && health < 100;
  if (healing) dHealth += HAZARD.REGEN;
  // HOW LONG UNTIL IT KILLS YOU, at this rate, for the warnings that name it ("about 40 s left"). Infinity when nothing
  // is taking health. Not counting the reserve still to spend: once it is spent the rate changes, and the reserve has
  // its own clock on screen.
  const secondsLeft = dHealth < 0 ? health / -dHealth : Infinity;

  return { hazards, dHealth, healing, breathable, breathing, dO2, tempC, pressureMbar: P, secondsLeft };
}

// The reserve crossing a line, reported ONCE, on the tick it happens, the way `died` is: 'low' a minute out, 'empty'
// when suffocation starts. The HUD toasts these directly. It cannot leave them to the hazard toast, which names only
// the worst hazard: on a world that is also freezing, the cold would win that slot every time and the player would
// never be told the air was running out.
function reserveEvent(before, after) {
  if (before > 0 && after <= 0) return 'empty';
  if (!o2Low(before) && o2Low(after)) return 'low';
  return null;
}

// Advance life support by dt seconds. Returns the NEW state plus what happened, so the caller decides what a death
// means (respawn, drop a stash) rather than this module reaching into the game.
// Clamps to 0..100 and reports `died` exactly once — on the transition, not on every tick at zero.
export function stepLifeSupport(ls, env, dt) {
  const d = Math.max(0, Math.min(1, dt || 0));   // cap at 1s: a backgrounded tab must not resume and instantly kill you
  const ev = evaluateHazards(env, ls);
  // SHELTERED: READ THE ENVIRONMENT, DO NOT SUFFER IT. The mining flyer is a craft, and a craft around an airless
  // rock was killing its pilot on a loop — flyerEnvFor reported vacuum every 200ms, anoxia is critical, and a
  // respawn put you straight back in it. (The exitToOrbit comment in system-view.js documents that loop.)
  //
  // The honest fix is NOT to stop reporting. What is outside is exactly what a pilot wants to know before they
  // decide to land, so the hazards, the temperature and the pressure all still come back and the readout still
  // says "Vacuum outside". Only the CONSEQUENCE is suppressed, and only downward: healing still applies, because
  // sitting in a sealed craft is a reasonable place to recover, and clamping that away would make shelter a
  // penalty of its own. (The air is the one consequence evaluateHazards settles itself, because it is not merely
  // suppressed in a craft but reversed: the pilot breathes the flyer's air, and the reserve refills.)
  const dHealth = env && env.sheltered ? Math.max(0, ev.dHealth) : ev.dHealth;
  const prev = healthOf(ls);
  const o2Prev = o2Of(ls);
  const next = { health: Math.max(0, Math.min(100, prev + dHealth * d)), o2: o2Clamp(o2Prev + ev.dO2 * d) };
  return { ls: next, hazards: ev.hazards, died: prev > 0 && next.health <= 0,
           o2Event: reserveEvent(o2Prev, next.o2),
           healthEvent: dHealth < 0 ? healthEvent(prev, next.health) : null,
           rates: Object.assign({}, ev, { dHealth, secondsLeft: dHealth < 0 ? next.health / -dHealth : Infinity }) };
}

// HEALTH CROSSING ITS TWO LINES, reported once each on the tick it happens, like the reserve's (QA F-30): 'low' at
// HEALTH_LOW and 'critical' at HEALTH_CRITICAL, only while something is taking health (mending back up past a line is
// not news). The HUD toasts them with the time left, sounds them, and pulses the screen's edges from 'low' on.
function healthEvent(before, after) {
  if (before > HAZARD.HEALTH_CRITICAL && after <= HAZARD.HEALTH_CRITICAL && after > 0) return 'critical';
  if (before > HAZARD.HEALTH_LOW && after <= HAZARD.HEALTH_LOW && after > 0) return 'low';
  return null;
}

// SEALED: in the ship. Orbit, the system and galaxy views, anywhere off a world's surface: nothing outside reaches the
// suit, so there is nothing to report, and the ship puts it right: the reserve refills in O2_SHIP_REFILL_S and health
// mends at SHIP_REGEN (QA F-43: it used to HOLD here, and the ship refilled no faster than breathable air).
// Kept apart from stepLifeSupport rather than spelled as a null environment, because evaluateHazards has always read
// a missing environment as one with no air, and "no reading" must not quietly start meaning "safe" anywhere else.
//
// dt is NOT capped at a second here. That cap exists so a backgrounded tab cannot resume and kill you, and a refill
// cannot hurt anyone, while a browser throttles a hidden tab's timers to one tick a second or slower: capped, a player
// who left the game in orbit would come back to a tank that had barely moved. Ten minutes in the ship is a full tank.
export function stepSealed(ls, dt) {
  // Past a full refill and a full mend, more time changes nothing.
  const d = Math.max(0, Math.min(Math.max(HAZARD.O2_SHIP_REFILL_S, 100 / HAZARD.SHIP_REGEN), dt || 0));
  const o2Prev = o2Of(ls), hPrev = healthOf(ls);
  const dO2 = o2Prev < 100 ? 100 / HAZARD.O2_SHIP_REFILL_S : 0;
  const dHealth = hPrev < 100 ? HAZARD.SHIP_REGEN : 0;
  return { ls: { health: Math.min(100, hPrev + dHealth * d), o2: o2Clamp(o2Prev + dO2 * d) },
           rates: { dO2, dHealth, healing: dHealth > 0, breathing: 'ship' } };
}

export function freshLifeSupport() { return { health: 100, o2: 100 }; }
